<template>
    <div class="dev-tickets-page">
        <!-- header -->
        <b-card no-body class="dt-header-card mb-1">
            <div class="dt-header">
                <div class="dt-header-text">
                    <h2 class="mb-25">
                        <feather-icon icon="ClipboardIcon" size="22" class="mr-50"/>
                        {{ msg('Dev Tickets') }}
                    </h2>
                    <span class="dt-subtitle">{{ msg('Report bugs, request features and track tasks') }}</span>
                </div>
                <b-button v-if="canCreate" variant="primary" @click="openCreate">
                    <feather-icon icon="PlusIcon" size="16" class="mr-50"/>{{ msg('New ticket') }}
                </b-button>
            </div>

            <!-- filters -->
            <div class="dt-filters">
                <b-input-group class="dt-search">
                    <b-input-group-prepend is-text>
                        <feather-icon icon="SearchIcon"/>
                    </b-input-group-prepend>
                    <b-form-input v-model="filters.search" :placeholder="msg('Search by title or description')"
                                  @input="onSearchInput"/>
                </b-input-group>

                <v-select v-model="filters.statuses" :options="statusOptions" :reduce="o => o.value"
                          label="label" multiple append-to-body class="dt-filter"
                          :placeholder="msg('Status')" @input="reload"/>
                <v-select v-model="filters.types" :options="typeOptions" :reduce="o => o.value"
                          label="label" multiple append-to-body class="dt-filter"
                          :placeholder="msg('Type')" @input="reload"/>
                <v-select v-model="filters.priorities" :options="priorityOptions" :reduce="o => o.value"
                          label="label" multiple append-to-body class="dt-filter"
                          :placeholder="msg('Priority')" @input="reload"/>
                <b-button variant="flat-secondary" class="dt-reset" @click="resetFilters">
                    <feather-icon icon="RefreshCwIcon" size="14" class="mr-25"/>{{ msg('Reset') }}
                </b-button>
            </div>
        </b-card>

        <!-- loading -->
        <div v-if="loading" class="text-center py-4">
            <b-spinner variant="primary"/>
        </div>

        <!-- empty -->
        <b-card v-else-if="!tickets.length" class="dt-empty text-center py-4">
            <feather-icon icon="InboxIcon" size="42" class="text-muted mb-1"/>
            <h4>{{ msg('No tickets found') }}</h4>
            <p class="text-muted mb-1">{{ msg('Try adjusting search or filters') }}</p>
            <b-button v-if="canCreate" variant="outline-primary" @click="openCreate">
                <feather-icon icon="PlusIcon" size="16" class="mr-50"/>{{ msg('New ticket') }}
            </b-button>
        </b-card>

        <!-- card grid -->
        <b-row v-else>
            <b-col v-for="t in tickets" :key="t.id" cols="12" md="6" xl="4" class="mb-1">
                <b-card no-body class="dt-card h-100" @click="openTicket(t)">
                    <div class="dt-card-stripe" :class="'bg-' + badge(statuses, t.status)"></div>
                    <div class="dt-card-body">
                        <div class="dt-card-top">
                            <span class="dt-card-id">#{{ t.id }}</span>
                            <b-badge :variant="badge(statuses, t.status)" pill>{{ msg(label(statuses, t.status)) }}</b-badge>
                        </div>
                        <h5 class="dt-card-title">{{ t.title }}</h5>
                        <p class="dt-card-desc">{{ truncate(t.description, 110) }}</p>

                        <div class="dt-card-tags">
                            <b-badge :variant="'light-' + badge(types, t.type)" class="mr-50">
                                {{ msg(label(types, t.type)) }}
                            </b-badge>
                            <b-badge :variant="'light-' + badge(priorities, t.priority)" class="mr-50">
                                {{ msg(label(priorities, t.priority)) }}
                            </b-badge>
                            <b-badge variant="light-secondary">{{ msg(label(modules, t.module)) }}</b-badge>
                        </div>

                        <div class="dt-card-foot">
                            <span class="dt-foot-user">
                                <feather-icon icon="UserIcon" size="13" class="mr-25"/>{{ t.createdByName || ('#' + t.createdBy) }}
                            </span>
                            <span class="dt-foot-meta">
                                <span class="mr-1"><feather-icon icon="MessageSquareIcon" size="13" class="mr-25"/>{{ (t.comments || []).length }}</span>
                                <span v-if="t.attachments && t.attachments.length" class="mr-1">
                                    <feather-icon icon="PaperclipIcon" size="13" class="mr-25"/>{{ t.attachments.length }}
                                </span>
                                <span>{{ formatDate(t.creationTime) }}</span>
                            </span>
                        </div>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <!-- pagination -->
        <div v-if="!loading && total > amount" class="d-flex justify-content-center mt-1">
            <b-pagination v-model="page" :total-rows="total" :per-page="amount" first-number last-number
                          @input="fetch"/>
        </div>

        <dev-ticket-modal v-model="showModal" :ticket="selectedTicket" @saved="onSaved"/>
    </div>
</template>

<script>
import vSelect from 'vue-select'
import DevTicketModal from '@/components/modals/devTicket/DevTicketModal.vue'
import {
    DEV_TICKET_TYPES, DEV_TICKET_STATUSES, DEV_TICKET_PRIORITIES, DEV_TICKET_MODULES,
    labelFor, variantFor,
} from '@/model/devTicket/devTicketEnums'

export default {
    name: 'DevTickets',
    components: {vSelect, DevTicketModal},
    data() {
        return {
            tickets: [],
            loading: false,
            page: 1,
            amount: 12,
            total: 0,
            filters: {search: '', statuses: [], types: [], priorities: []},
            searchTimer: null,
            showModal: false,
            selectedTicket: null,
            types: DEV_TICKET_TYPES,
            statuses: DEV_TICKET_STATUSES,
            priorities: DEV_TICKET_PRIORITIES,
            modules: DEV_TICKET_MODULES,
        }
    },
    computed: {
        canCreate() {
            return this.$store.getters['user/canCreate']('dev_ticket');
        },
        // Pre-translated v-select options (label="label"), matching the project pattern.
        statusOptions() {
            return this.statuses.map((o) => ({value: o.value, label: this.msg(o.label)}));
        },
        typeOptions() {
            return this.types.map((o) => ({value: o.value, label: this.msg(o.label)}));
        },
        priorityOptions() {
            return this.priorities.map((o) => ({value: o.value, label: this.msg(o.label)}));
        },
    },
    created() {
        this.fetch();
        this.$store.dispatch('devTicket/loadAssignableUsers');
    },
    methods: {
        fetch() {
            this.loading = true;
            const filter = {
                search: this.filters.search || null,
                statuses: this.filters.statuses.length ? this.filters.statuses : null,
                types: this.filters.types.length ? this.filters.types : null,
                priorities: this.filters.priorities.length ? this.filters.priorities : null,
            };
            this.$store.dispatch('devTicket/fetchAll', {
                filter, page: this.page, amount: this.amount,
                sortby: 'desc', sortcolumn: 'creationTime',
            }).then((ans) => {
                let list = ans && ans['DevTickets'] ? ans['DevTickets'] : [];
                if (!Array.isArray(list)) list = Object.values(list);
                this.tickets = list;
                this.total = (ans && (ans['Total'] != null ? ans['Total'] : ans['TotalRows'])) || list.length;
                this.loading = false;
            }).catch(() => {
                this.loading = false;
            });
        },
        reload() {
            this.page = 1;
            this.fetch();
        },
        onSearchInput() {
            if (this.searchTimer) clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.reload(), 500);
        },
        resetFilters() {
            this.filters = {search: '', statuses: [], types: [], priorities: []};
            this.reload();
        },
        openCreate() {
            this.selectedTicket = null;
            this.showModal = true;
        },
        openTicket(t) {
            this.selectedTicket = t;
            this.showModal = true;
        },
        onSaved() {
            this.fetch();
        },
        truncate(s, n) {
            if (!s) return '';
            return s.length > n ? s.slice(0, n).trim() + '…' : s;
        },
        label(list, v) { return labelFor(list, v); },
        badge(list, v) { return variantFor(list, v); },
        formatDate(value) {
            if (!value) return '';
            let d;
            if (Array.isArray(value)) {
                d = new Date(value[0], (value[1] || 1) - 1, value[2] || 1, value[3] || 0, value[4] || 0, value[5] || 0);
            } else {
                d = new Date(value);
            }
            if (isNaN(d.getTime())) return '';
            return d.toLocaleDateString(undefined, {month: 'short', day: '2-digit', year: 'numeric'});
        },
    },
}
</script>

<style lang="scss" scoped>
.dt-header-card {
    padding: 1.25rem 1.5rem;
}

.dt-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem;
}

.dt-subtitle {
    color: #b9b9c3;
    font-size: .9rem;
}

.dt-filters {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: .75rem;
    margin-top: 1.25rem;
    padding-top: 1.25rem;
    border-top: 1px solid #ebe9f1;
}

.dt-search {
    flex: 1 1 240px;
    max-width: 360px;
}

.dt-filter {
    flex: 1 1 160px;
    min-width: 150px;
}

.dt-reset {
    margin-left: auto;
}

/* cards */
.dt-card {
    cursor: pointer;
    overflow: hidden;
    transition: transform .15s ease, box-shadow .15s ease;
    border: 1px solid #ebe9f1;
}

.dt-card:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 22px rgba(34, 41, 47, .12);
}

.dt-card-stripe {
    height: 5px;
    width: 100%;
}

.dt-card-body {
    padding: 1rem 1.15rem 1.15rem;
    display: flex;
    flex-direction: column;
    height: calc(100% - 5px);
}

.dt-card-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: .35rem;
}

.dt-card-id {
    font-weight: 700;
    color: #b9b9c3;
    font-size: .85rem;
}

.dt-card-title {
    font-weight: 700;
    margin-bottom: .35rem;
    color: #5e5873;
    line-height: 1.3;
}

.dt-card-desc {
    color: #6e6b7b;
    font-size: .85rem;
    line-height: 1.45;
    margin-bottom: .75rem;
    flex: 1;
}

.dt-card-tags {
    margin-bottom: .75rem;
}

.dt-card-foot {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top: 1px solid #f1f0f5;
    padding-top: .6rem;
    font-size: .78rem;
    color: #b9b9c3;
}

.dt-foot-user {
    font-weight: 600;
    color: #6e6b7b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 50%;
}

.dt-foot-meta {
    display: flex;
    align-items: center;
    white-space: nowrap;
}

/* dark mode */
.dark-layout {
    .dt-subtitle, .dt-card-id, .dt-card-foot { color: #676d7d; }
    .dt-filters { border-top-color: #3b4253; }
    .dt-card { border-color: #3b4253; background: #283046; }
    .dt-card-title { color: #d0d2d6; }
    .dt-card-desc, .dt-foot-user { color: #b4b7bd; }
    .dt-card-foot { border-top-color: #3b4253; }
    .dt-card:hover { box-shadow: 0 8px 22px rgba(0, 0, 0, .4); }
}
</style>
